import { ELayoutPosition } from "@kuika/kuika-cl-model";

/** A master screen's four shell regions: header, footer, left sidebar, right sidebar. */
export type MasterLayoutPart = "h" | "f" | "l" | "r";

/** Where each shell region sits. `primary` is the outer ring, `secondary` the inner one. */
export interface IMasterLayout {
  h: ELayoutPosition;
  f: ELayoutPosition;
  l: ELayoutPosition;
  r: ELayoutPosition;
}

export const MASTER_LAYOUT_PART_NAMES: Record<MasterLayoutPart, string> = {
  h: "Header",
  f: "Footer",
  l: "Left sidebar",
  r: "Right sidebar"
};

const KEY_PATTERN: RegExp = /^h([012])-f([012])-l([012])-r([012])$/;

export const masterLayoutKey = (layout: IMasterLayout): string => `h${layout.h}-f${layout.f}-l${layout.l}-r${layout.r}`;

export const parseMasterLayoutKey = (key: string): IMasterLayout | undefined => {
  const match: RegExpExecArray | null = KEY_PATTERN.exec(key);
  if (!match) return undefined;
  return {
    h: Number(match[1]) as ELayoutPosition,
    f: Number(match[2]) as ELayoutPosition,
    l: Number(match[3]) as ELayoutPosition,
    r: Number(match[4]) as ELayoutPosition
  };
};

/**
 * Whether a header or footer at `bar` is drawn across a sidebar at `side`. An outer bar spans
 * everything; an inner one spans only a sidebar that is itself inner (or absent).
 */
const barSpansSidebar = (bar: ELayoutPosition, side: ELayoutPosition): boolean =>
  bar !== ELayoutPosition.invisible && (bar === ELayoutPosition.primary || side !== ELayoutPosition.primary);

const describeBar = (bar: ELayoutPosition, layout: IMasterLayout): string => {
  const spansLeft: boolean = barSpansSidebar(bar, layout.l);
  const spansRight: boolean = barSpansSidebar(bar, layout.r);
  if (spansLeft && spansRight) return "Full width";
  if (spansLeft) return "Next to right sidebar";
  if (spansRight) return "Next to left sidebar";
  return "Between sidebars";
};

const describeSidebar = (side: ELayoutPosition, layout: IMasterLayout): string => {
  const underHeader: boolean = barSpansSidebar(layout.h, side);
  const overFooter: boolean = barSpansSidebar(layout.f, side);
  if (underHeader && overFooter) return "Between header and footer";
  if (underHeader) return "Below header";
  if (overFooter) return "Above footer";
  return "Full height";
};

/** What the preview draws for one region, in the words the options menu shows. */
export const describeLayoutRegion = (part: MasterLayoutPart, layout: IMasterLayout): string => {
  const position: ELayoutPosition = layout[part];
  if (position === ELayoutPosition.invisible) return "Off";
  if (part === "h" || part === "f") return describeBar(position, layout);
  return describeSidebar(position, layout);
};
